import './styles/CreateArticlePage.scss';

import { InfoOutlined } from '@mui/icons-material';
import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Paper,
  TextField,
  Tooltip
} from '@mui/material';
import { Article, UpdateArticle } from '@thro-types/leihbar';

import { Field } from 'formik';
import { omit } from 'lodash';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { useLeihbarArticleMutation } from 'api/hooks/leihbar';
import CreatePage from 'app/components/CreatePage';
import StepDescription from 'app/components/CreatePage/components/StepDescription';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';

import { articleValidationSchema, useArticleInitialValues } from './validation';

const CreateArticlePage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const productId = location.state?.productId;
  const article: Article = location.state?.article;

  usePageHeading({
    heading: article?.id
      ? t(translations.pages.leihbar.article.create.editHeading())
      : t(translations.pages.leihbar.article.create.createHeading()),
    headingProps: { back: location.state?.back ?? `/leihbar/product/${productId}` }
  });

  const [submitting, setSubmitting] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);

  const { create, update } = useLeihbarArticleMutation();

  const handleSaveClick = async (values: UpdateArticle & { id?: number }) => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    if (setDisabledCancel) {
      setDisabledCancel(true);
    }

    if (values.id) {
      try {
        const omitFields = ['id', 'product'] as const;
        let saveBody = omit(values, omitFields) as Omit<UpdateArticle, (typeof omitFields)[number]>;

        await update.mutateAsync({ id: values.id, data: saveBody });
        dispatch(
          AlertActions.success(t(translations.pages.leihbar.article.alerts.successfulUpdate()))
        );
      } catch (error) {
        dispatch(AlertActions.error(t(translations.pages.leihbar.article.alerts.failedUpdate())));
      }
    } else {
      try {
        values.productId = parseInt(productId);

        await create.mutateAsync(values);
        dispatch(
          AlertActions.success(t(translations.pages.leihbar.article.alerts.successfulCreate()))
        );
      } catch (error) {
        dispatch(AlertActions.error(t(translations.pages.leihbar.article.alerts.failedCreate())));
      }
    }
    setSubmitting(false);
    navigate(-1);
  };

  return (
    <>
      <Helmet>
        <title>{t(translations.pages.leihbar.article.name())}</title>
      </Helmet>
      <CreatePage<UpdateArticle & { id?: number }>
        initialValues={useArticleInitialValues(article)}
        validationSchema={articleValidationSchema}
        disabledCancel={disabledCancel}
        handleSaveClick={handleSaveClick}
        submittingState={[submitting, setSubmitting]}
      >
        {({ values, touched, errors, setFieldValue }) => (
          <Grid container className="article-create-grid">
            <Grid container>
              <StepDescription
                title={t(translations.pages.leihbar.article.create.steps.articleId.title())}
                text={t(translations.pages.leihbar.article.create.steps.articleId.text())}
              />
              <Grid container item component={Paper} xs={12} xl={6}>
                <Grid item xs={6}>
                  <FormControl required>
                    <FormLabel>
                      {t(translations.pages.leihbar.article.create.articleId())}
                    </FormLabel>
                    <Field
                      as={TextField}
                      id="sku"
                      name="sku"
                      error={touched.sku && Boolean(errors.sku)}
                      helperText={touched.sku && errors.sku}
                      size="small"
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={10}>
              <Divider />
            </Grid>
            <Grid container>
              <StepDescription
                title={t(translations.pages.leihbar.article.create.steps.isVisible.title())}
                text={t(translations.pages.leihbar.article.create.steps.isVisible.text())}
              />
              <Grid container item component={Paper} xs={12} xl={6}>
                <FormControl fullWidth>
                  <Grid container flexDirection={'row'}>
                    <Grid item>
                      <FormLabel>
                        {t(translations.pages.leihbar.article.create.isVisible())}
                      </FormLabel>
                      <Grid item xs={12}>
                        <FormControlLabel
                          id="isVisible"
                          name="isVisible"
                          control={
                            <Checkbox
                              value={values.isVisible}
                              checked={values.isVisible}
                              onChange={(_, checked) => setFieldValue('isVisible', checked)}
                            />
                          }
                          label={t(translations.pages.leihbar.article.create.isVisible())}
                        />
                      </Grid>
                    </Grid>
                    {values.id && (
                      <Grid container item xs={4} flex={1} alignItems={'center'} marginLeft={3}>
                        <Tooltip
                          title={t(
                            translations.pages.leihbar.article.create.steps.isVisible.editInfo()
                          )}
                        >
                          <InfoOutlined fontSize="large" style={{ opacity: 0.8 }} />
                        </Tooltip>
                      </Grid>
                    )}
                  </Grid>
                </FormControl>
              </Grid>
            </Grid>
            <Grid item xs={10}>
              <Divider />
            </Grid>
            <Grid container>
              <StepDescription
                title={t(translations.pages.leihbar.article.create.steps.internalNote.title())}
                text={t(translations.pages.leihbar.article.create.steps.internalNote.text())}
              />
              <Grid item container component={Paper} xs={12} xl={6} direction={'column'}>
                <FormControl style={{ paddingBottom: '1em' }}>
                  <FormLabel>{t(translations.pages.leihbar.internalArticleNote())}</FormLabel>
                  <Field
                    as={TextField}
                    id="internalNote"
                    name="internalNote"
                    fullWidth
                    size="small"
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        )}
      </CreatePage>
    </>
  );
};

export default CreateArticlePage;
